/**
 * Раскрытие строки темы (#845 PR 6) выше списка разговоров: определение темы,
 * «тема × категория звонка» и срезы, которые прислал сервер, — качество и
 * отказы («Продажи»), вопросы без ответа («Сайт и контент»). Чего в ответе нет,
 * того нет и здесь: срез не нарисован, а не обнулён.
 */
import { useTranslation } from 'react-i18next';
import { VoiceItemList } from './VoiceItemList';
import type { VoiceThemeExtra } from './voice-themes-view-model';

interface VoiceThemeDetailsProps {
  theme: VoiceThemeExtra;
  /** K строки: «вопросы без ответа — в U из K разговоров». */
  calls: number;
  onOpenCall: (recordId: string) => void;
}

export function VoiceThemeDetails({ theme, calls, onOpenCall }: VoiceThemeDetailsProps) {
  const { t } = useTranslation(['dashboard', 'records']);
  const { quality, declines, unanswered } = theme;
  return (
    <div className="space-y-3">
      {theme.definition !== '' && (
        <p className="text-sm text-fg-muted">{theme.definition}</p>
      )}

      {theme.categories.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-fg">
            {t('voicePage.themes.analytics.byCategory')}
          </p>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-fg-muted">
            {theme.categories.map((entry) => (
              <li key={entry.category}>
                {t('voicePage.themes.analytics.categoryCount', {
                  category: t(`records:records.callCategory.${entry.category}`),
                  count: entry.calls,
                })}
              </li>
            ))}
          </ul>
        </div>
      )}

      {quality && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-fg">
            {t('voicePage.themes.analytics.quality.title')}
          </p>
          <p className="text-xs text-fg-muted">
            {quality.score === null
              ? t('voicePage.themes.analytics.quality.none')
              : quality.overallScore === null
                ? t('voicePage.themes.analytics.quality.score', {
                    score: quality.score,
                    count: quality.scoredCalls,
                  })
                : t('voicePage.themes.analytics.quality.scoreVsOverall', {
                    score: quality.score,
                    overall: quality.overallScore,
                    count: quality.scoredCalls,
                  })}
          </p>
          {quality.smallSample && (
            <p className="text-xs text-fg-subtle">{t('voicePage.smallSample')}</p>
          )}
        </div>
      )}

      {declines && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-fg">
            {t('voicePage.themes.analytics.declines.title')}
          </p>
          <p className="text-xs text-fg-muted">
            {declines.sharePercent === null
              ? t('voicePage.themes.analytics.declines.noSales')
              : t('voicePage.themes.analytics.declines.line', {
                  declined: declines.declinedCalls,
                  count: declines.salesCalls,
                  share: declines.sharePercent,
                })}
          </p>
        </div>
      )}

      {unanswered && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-fg">
            {t('voicePage.themes.analytics.unanswered.title')}
          </p>
          <p className="text-xs text-fg-muted">
            {t('voicePage.themes.analytics.unanswered.line', {
              unanswered: unanswered.calls,
              count: calls,
            })}
          </p>
          {unanswered.items.length > 0 && (
            <VoiceItemList
              row={{ kind: 'theme' }}
              items={unanswered.items}
              onOpenCall={onOpenCall}
            />
          )}
          {unanswered.items.length > 0 && unanswered.calls > unanswered.items.length && (
            <p className="text-xs text-fg-subtle">
              {t('voicePage.shownOf', {
                shown: unanswered.items.length,
                count: unanswered.calls,
              })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
